import { useMemo } from 'react';
import { FeatureFlagName } from 'shared/enums';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import {
  BitwerxSystem,
  ClinicPimsIntegrationFragment,
  ClinicPimsIntegrationType,
  SyncVetIntegrationSystem,
  VetDataIntegrationSystem,
} from 'shared/types/graphql';
import { IntegrationSystemName } from '../types/IntegrationSystem';

interface UseSupportedIntegrationFeaturesReturnType {
  isPostcardsSupported: boolean;
  isBillingCodeSupported: boolean;
  isClientIdSupported: boolean;
  isContactSyncSupported: boolean;
  isInvoicePreviewSupported: boolean;
  isInvoiceDiscoverySupported: boolean;
  isExportToPimsSupported: boolean;
  isMergeExistingPimsProfileSupported: boolean;
  isPatientIdSupported: boolean;
  isServiceCodeSupported: boolean;
  isTriggerPimsSyncSupported: boolean;
  isWritebackApptNotesSupported: boolean;
  isAppointmentRescheduleSupported: boolean;
  isAppointmentEditWritebackSupported: boolean;
  isAppointmentCreateWritebackSupported: boolean;
  isServiceRemindersDisabledForEzyvet: boolean;
}

export const useSupportedIntegrationFeatures = (
  primaryIntegrationType?: ClinicPimsIntegrationType,
  primarySystemName?: IntegrationSystemName | null,
  allActiveIntegrations?: ClinicPimsIntegrationFragment[],
): UseSupportedIntegrationFeaturesReturnType => {
  const { isFeatureEnabled } = useFeatureFlag();

  // Postcards
  const isPostcardsSupported =
    primaryIntegrationType !== ClinicPimsIntegrationType.Datapoint &&
    (primaryIntegrationType !== ClinicPimsIntegrationType.Ezyvet ||
      !isFeatureEnabled(FeatureFlagName.EnableServiceRemindersForEzyVet));

  // Billing Code
  const isBillingCodeSupported =
    primarySystemName !== VetDataIntegrationSystem.Impromed &&
    (primaryIntegrationType !== ClinicPimsIntegrationType.Ezyvet ||
      !isFeatureEnabled(FeatureFlagName.EnableServiceRemindersForEzyVet));

  // Service Code
  const isServiceCodeSupported =
    primarySystemName === VetDataIntegrationSystem.Cornerstone ||
    primaryIntegrationType === ClinicPimsIntegrationType.Datapoint;

  // Client ID
  const isClientIdSupported =
    primarySystemName === VetDataIntegrationSystem.Cornerstone ||
    primaryIntegrationType === ClinicPimsIntegrationType.Datapoint ||
    primarySystemName === VetDataIntegrationSystem.Avimark;

  // Patient ID
  const isPatientIdSupported =
    primarySystemName === VetDataIntegrationSystem.Cornerstone ||
    primaryIntegrationType === ClinicPimsIntegrationType.Datapoint;

  // Contact Sync
  const isContactSyncSupported = primaryIntegrationType === ClinicPimsIntegrationType.Datapoint;

  // Invoice Preview
  const isInvoicePreviewSupported =
    primarySystemName === VetDataIntegrationSystem.Impromed ||
    primarySystemName === VetDataIntegrationSystem.Cornerstone ||
    primaryIntegrationType === ClinicPimsIntegrationType.Datapoint;

  // Invoice Discovery
  const isInvoiceDiscoverySupported =
    primarySystemName === VetDataIntegrationSystem.Impromed ||
    primarySystemName === VetDataIntegrationSystem.Cornerstone ||
    primarySystemName === VetDataIntegrationSystem.Avimark ||
    primaryIntegrationType === ClinicPimsIntegrationType.Datapoint;

  // Merge Existing PIMS Profile
  const isMergeExistingPimsProfileSupported = primarySystemName === VetDataIntegrationSystem.Avimark;

  // Trigger PIMS Sync
  const isTriggerPimsSyncSupported =
    primaryIntegrationType === ClinicPimsIntegrationType.Instinct ||
    primaryIntegrationType === ClinicPimsIntegrationType.Vetdata;

  // Appointment reschedule
  const isAppointmentRescheduleSupported = primaryIntegrationType !== ClinicPimsIntegrationType.Neo;

  // Appointment Edit Writeback
  const isAppointmentEditWritebackSupported = useMemo(() => {
    if (primaryIntegrationType) {
      // Datapoint (Cornerstone), Vetdata (Evet), and Neo are eleigible for the appointment creation writeback ff shown in Otto labs
      // If this feature is on, changes to the appointment will be written back to the PIMS
      if (
        primaryIntegrationType === ClinicPimsIntegrationType.Neo ||
        primaryIntegrationType === ClinicPimsIntegrationType.Datapoint ||
        (primaryIntegrationType === ClinicPimsIntegrationType.Vetdata &&
          primarySystemName === VetDataIntegrationSystem.Evetpractice)
      ) {
        return isFeatureEnabled(FeatureFlagName.FlowAppointmentCreationPimsWriteback);
      }

      // Otherwise, all other PIMS do not support appointment create/update writebacks
      // VetData/Bitwerx (Impromed, Avimark, Cornerstone), Instinct, Ezyvet and any other integration type
      return false;
    }

    // If the clinic is PIMS-less than there's no writeback needed for appointment update/create
    // So yes the capability is supported
    return true;
  }, [primaryIntegrationType, isFeatureEnabled, primarySystemName]);

  // Appointment Create Writeback
  const isAppointmentCreateWritebackSupported = useMemo(() => {
    if (!primaryIntegrationType) return false;
    if ([ClinicPimsIntegrationType.Neo, ClinicPimsIntegrationType.Ezyvet].includes(primaryIntegrationType)) {
      return true;
    }

    const bitwerxIntegration = allActiveIntegrations?.find(
      (integration) => integration.type === ClinicPimsIntegrationType.Bitwerx,
    );

    if (
      bitwerxIntegration?.bitwerx?.system &&
      [BitwerxSystem.Avimark, BitwerxSystem.Cornerstone].includes(bitwerxIntegration.bitwerx?.system)
    ) {
      return true;
    }

    const supportedSystems: IntegrationSystemName[] = [
      VetDataIntegrationSystem.Avimark,
      VetDataIntegrationSystem.Impromed,
    ];

    if (primarySystemName && supportedSystems.includes(primarySystemName)) {
      return true;
    }
    return false;
  }, [primaryIntegrationType, allActiveIntegrations, primarySystemName]);

  // Writeback Appt Notes
  const isWritebackApptNotesSupported = useMemo(() => {
    if (!primaryIntegrationType) false;

    switch (primaryIntegrationType) {
      case ClinicPimsIntegrationType.Vetdata:
        if (!primarySystemName) return false;
        switch (primarySystemName) {
          case VetDataIntegrationSystem.Avimark:
          case VetDataIntegrationSystem.Impromed:
          case VetDataIntegrationSystem.Cornerstone:
            return true;
          default:
            return false;
        }
      case ClinicPimsIntegrationType.SyncVet:
        switch (primarySystemName) {
          case SyncVetIntegrationSystem.Avimark:
          case SyncVetIntegrationSystem.Cornerstone:
          case SyncVetIntegrationSystem.Impromed:
          case SyncVetIntegrationSystem.Shepherd:
          case SyncVetIntegrationSystem.Vetspire:
            return true;
          default:
            return false;
        }
      default:
        return false;
    }
  }, [primaryIntegrationType, primarySystemName]);

  // Export to PIMS
  const isExportToPimsSupported = useMemo(() => {
    if (!primaryIntegrationType) false;

    switch (primaryIntegrationType) {
      case ClinicPimsIntegrationType.Neo:
      case ClinicPimsIntegrationType.Ezyvet:
      case ClinicPimsIntegrationType.Datapoint:
      case ClinicPimsIntegrationType.Bitwerx:
        return true;
      case ClinicPimsIntegrationType.Vetdata:
        if (!primarySystemName) return false;
        switch (primarySystemName) {
          case VetDataIntegrationSystem.Evetpractice:
          case VetDataIntegrationSystem.Avimark:
          case VetDataIntegrationSystem.Impromed:
          case VetDataIntegrationSystem.Cornerstone:
            return true;
          default:
            return false;
        }
      case ClinicPimsIntegrationType.SyncVet:
        switch (primarySystemName) {
          case SyncVetIntegrationSystem.Avimark:
          case SyncVetIntegrationSystem.Cornerstone:
          case SyncVetIntegrationSystem.Vetspire:
          case SyncVetIntegrationSystem.Shepherd:
            return true;
          default:
            return false;
        }
      case ClinicPimsIntegrationType.Instinct:
        return true;
      case ClinicPimsIntegrationType.Vetter:
        return false;
      default:
        return false;
    }
  }, [primaryIntegrationType, primarySystemName]);

  // SR for ezyVet
  const isServiceRemindersDisabledForEzyvet =
    primaryIntegrationType === ClinicPimsIntegrationType.Ezyvet &&
    !isFeatureEnabled(FeatureFlagName.EnableServiceRemindersForEzyVet);

  return {
    isPostcardsSupported,
    isBillingCodeSupported,
    isClientIdSupported,
    isPatientIdSupported,
    isInvoicePreviewSupported,
    isInvoiceDiscoverySupported,
    isContactSyncSupported,
    isMergeExistingPimsProfileSupported,
    isExportToPimsSupported,
    isServiceCodeSupported,
    isAppointmentRescheduleSupported,
    isTriggerPimsSyncSupported,
    isWritebackApptNotesSupported,
    isAppointmentEditWritebackSupported,
    isAppointmentCreateWritebackSupported,
    isServiceRemindersDisabledForEzyvet,
  };
};
